import supabase from "@/auth/Client";
import { getViewAsUserId, VIEW_AS_USER_HEADER } from "@/auth/viewAs";

const apiHost = import.meta.env.VITE_API_HOST;

// Simple auth failure counter with timing
let authFailures: number[] = []; // Store timestamps of failures

export const apiFetch = async (path: string | URL, init?: RequestInit): Promise<Response> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers: Record<string, string> = {};

  // Copy existing headers
  if (init?.headers) {
    // HeadersInit can be Headers, string[][], or Record<string, string>
    // We need to handle each case to flatten it into our record.
    if (init.headers instanceof Headers) {
      init.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (Array.isArray(init.headers)) {
      init.headers.forEach(([key, value]) => {
        headers[key] = value;
      });
    } else {
      Object.assign(headers, init.headers);
    }
  }

  // Add Authorization header if it exists
  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }

  const viewAsUserId = getViewAsUserId();
  if (viewAsUserId) {
    headers[VIEW_AS_USER_HEADER] = viewAsUserId;
  }

  // Add Content-Type if we're sending a JSON body
  if (init?.body && typeof init.body === "string") {
    headers["Content-Type"] = "application/json";
  }

  const _init = {
    ...init,
    credentials: "include" as RequestCredentials,
    headers,
  };

  const url = new URL(path, apiHost);
  const response = await fetch(url, _init);

  // Signing out here would log the admin out of every tab, since the Supabase session is shared.
  if (viewAsUserId) {
    return response;
  }

  // Handle auth failures
  if (response.status === 401 || response.status === 403) {
    const now = Date.now();

    // Add current failure timestamp
    authFailures.push(now);

    // Remove failures older than 10 seconds
    authFailures = authFailures.filter((timestamp) => now - timestamp < 10000);

    if (authFailures.length >= 3) {
      console.warn("3 auth failures within 10 seconds, logging out...");
      authFailures = []; // Reset

      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch (error) {
        console.warn("Logout failed:", error);
      }

      window.location.href = "/";
    }
  } else if (response.ok) {
    authFailures = []; // Reset on success
  }

  return response;
};

export const unAuthenticatedApiFetch = async (
  path: string | URL,
  init?: RequestInit
): Promise<Response> => {
  const url = new URL(path, apiHost);
  return await fetch(url, {
    ...init,
    credentials: "include" as RequestCredentials,
    headers: {
      ...init?.headers,
    },
  });
};
